import React from 'react';
import i18next from 'i18next';
import { ColumnActionsMode, IColumn, IDetailsColumnRenderTooltipProps, IDetailsHeaderProps } from '@fluentui/react/lib/DetailsList';
import { IRenderFunction } from '@fluentui/react/lib/Utilities';
import { ArrowDownIcon, ArrowUpIcon, CloseIcon, Dialog, MenuButton } from '@fluentui/react-northstar';
import AppCSS from '../../../App.module.css';
import { canRemoveColumn } from './listPreferences';

// Fluent's DetailsColumn records its position when it first subscribes to drag and drop and never
// updates it. That position counts the checkbox column, and the ticket list draws without one until
// its licence check finishes - so every header was recorded one place too far left (measured: all 30),
// Id could not be dragged at all, and a column could not be dropped in front of its neighbour. A drag,
// or a list laying out its saved order after the first render, stales it the same way. Re-mounting the
// header whenever the order or the checkbox column changes gives every column its current position.
// Only the header row is rebuilt; the list keeps its rows, scroll position and laid-out widths.
//
// Given header actions (sorting, removing), each header also gets a menu (ColumnMenu).
export interface ColumnHeaderActions {
  /** Sorts by the column, ascending or descending. Only offered on a column the list can sort. */
  onSortColumn?: (column: IColumn, descending: boolean) => void;
  /** Hides the column for this agent on this view, until Reset View. Never offered on Id, Request or the last column. */
  onRemoveColumn?: (columnKey: string) => void;
}

export const headerKeyedByColumnOrder = (
  columns: { key: string }[],
  actions?: ColumnHeaderActions
): IRenderFunction<IDetailsHeaderProps> => {
  const orderKey = columns.map(column => column.key).join('|');
  const onRenderColumnHeaderTooltip: IRenderFunction<IDetailsColumnRenderTooltipProps> = (tooltipProps, defaultRender) => {
    const column = tooltipProps.column;
    return defaultRender({
      ...tooltipProps,
      children: <>{tooltipProps.children}{column && <ColumnMenu column={column} shownColumnCount={columns.length} actions={actions} />}</>,
    });
  };
  return (props, defaultRender) => (
    <React.Fragment key={`${orderKey}:${props.selectionMode}:${props.selectAllVisibility}`}>
      {defaultRender(actions ? { ...props, onRenderColumnHeaderTooltip } : props)}
    </React.Fragment>
  );
};

// The narrowest column, as drawn, that still shows its menu: the title's 8px padding, a few letters, the sort arrows
// (18px) and the menu button with its space (34px - it sits 10px in). A column dragged narrower keeps its header to
// the title and arrows.
export const COLUMN_MENU_MIN_WIDTH = 86;

// The width a column is drawn at. Fluent's calculatedWidth leaves out the cell's padding: 12px left and 8px right
// (measured: Status 125 -> 145), and 24px more on a padded column (My Work's Type, 86 -> 130).
const drawnWidth = (column: IColumn) =>
  (column.calculatedWidth ?? column.currentWidth ?? column.minWidth) + 20 + (column.isPadded ? 24 : 0);

// The menu on a column header, as HubSpot's lists have: a vertical "more" button at the header's right, shown on
// hovering the header (and held while its menu is open), with Sort ascending, Sort descending and Remove column -
// room for more column actions later. The menu opens under the header, from the column's left edge and as wide as
// the column, but never narrower than its own items. A column with none of them gets no button. It sits in Fluent's full-cell layer
// around the title, beside the title rather than in it, so a click on it never reaches the title's sort; and a press
// on it never starts a column drag (startColumnDrag). Remove column asks first, as the app's other removals do.
//
// The menu and the dialog draw in portals, but React still bubbles their events through the header - a key pressed
// in the dialog would reach the header's focus zone, a click its sort - so they stop at the host span.
// Sent when a header is pressed anywhere but its menu button. The column drag cancels the press (so no text is
// selected), which also keeps the menu's own outside-click check from seeing it, so an open menu is closed this way.
const HEADER_PRESSED = 'tikit:column-header-pressed';

const ColumnMenu = ({ column, shownColumnCount, actions }: { column: IColumn; shownColumnCount: number; actions?: ColumnHeaderActions }) => {
  const [open, setOpen] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);
  // The header cell the menu is placed against, found from the button when it opens.
  const [cell, setCell] = React.useState<HTMLElement | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  // A press on any header closes it - a sort click, or the start of a column drag, which it would otherwise follow.
  React.useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener(HEADER_PRESSED, close);
    return () => window.removeEventListener(HEADER_PRESSED, close);
  }, [open]);
  const canSort = !!actions?.onSortColumn && column.columnActionsMode !== ColumnActionsMode.disabled;
  const canRemove = !!actions?.onRemoveColumn && canRemoveColumn(column.key, shownColumnCount);
  if ((!canSort && !canRemove) || drawnWidth(column) < COLUMN_MENU_MIN_WIDTH) return null;
  // The icons take the header's sort-arrow grey. The theme's colours are set on the list, and the menu draws outside
  // it (in a portal), so the colour is read from the header cell it opened from.
  const iconColor = cell ? getComputedStyle(cell).getPropertyValue('--mgt-theme-default-foreground2').trim() || undefined : undefined;
  const iconStyles = { color: iconColor };
  const t = (key: string, options?: Record<string, string>) => i18next.t(`ticket.ticket-list.configure-column.${key}`, options);
  const items = [
    ...(canSort ? [
      { key: 'asc', content: t('sort-ascending'), icon: <ArrowUpIcon size="small" styles={iconStyles} />, onClick: () => actions.onSortColumn(column, false) },
      { key: 'desc', content: t('sort-descending'), icon: <ArrowDownIcon size="small" styles={iconStyles} />, onClick: () => actions.onSortColumn(column, true) },
    ] : []),
    ...(canSort && canRemove ? [{ key: 'divider', kind: 'divider' as const }] : []),
    ...(canRemove ? [
      { key: 'remove', content: t('remove-column'), icon: <CloseIcon size="small" styles={iconStyles} />, onClick: () => setConfirming(true) },
    ] : []),
  ];
  const stop = (event: React.SyntheticEvent) => event.stopPropagation();
  return (
    <span className="column-menu-host" onClick={stop} onKeyDown={stop} onPointerDown={stop} onDoubleClick={stop}>
    <MenuButton
      open={open}
      onOpenChange={(_, data) => {
        if (data?.open) setCell(triggerRef.current?.closest<HTMLElement>('.ms-DetailsHeader-cell') ?? null);
        setOpen(!!data?.open);
      }}
      target={cell ?? undefined}
      position="below"
      align="start"
      offset={[0, 4]}
      trigger={
        <button
          ref={triggerRef}
          type="button"
          className={`column-menu${open ? ' is-open' : ''}`}
          aria-label={`${t('column-options')} ${column.name}`}
          onClick={event => event.stopPropagation()}
        />
      }
      menu={{ items, className: 'column-menu-list', styles: { width: cell ? `${cell.getBoundingClientRect().width}px` : undefined, minWidth: 'max-content' } }}
      onMenuItemClick={() => setOpen(false)}
    />
    {confirming && (
      <Dialog
        open
        className={AppCSS.confirmDialog}
        header={t('remove-column-confirm', { column: column.name })}
        content={t('remove-column-note', { resetView: t('reset-view') })}
        cancelButton={i18next.t('common.buttons.cancel')}
        confirmButton={{ content: t('remove'), primary: true }}
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          actions.onRemoveColumn(column.key);
        }}
      />
    )}
    </span>
  );
};

const DRAG_THRESHOLD = 4;
const EDGE = 48;
const ENTRY = 0.4;
let suppressNextClick = false;

// How far a column moves aside while another is dragged past it: a whole dragged column's width, towards where the
// dragged column started.
export const slideOffset = (i: number, from: number, target: number, width: number) => {
  if (i > from && i <= target) return -width;
  if (i < from && i >= target) return width;
  return 0;
};

// Where the slot the dragged column will land in starts, before the columns' own scroll is taken off.
export const dropSlotLeft = (rects: { left: number; right: number }[], from: number, target: number, width: number) => {
  if (target < from) return rects[target].left;
  if (target > from) return rects[target].right - width;
  return rects[from].left;
};

// Within EDGE of the scroll area's side the list scrolls that way, faster the nearer the pointer is to the edge.
export const edgeScrollSpeed = (x: number, left: number, right: number) => {
  if (x < left + EDGE) return -Math.ceil((left + EDGE - x) / 4);
  if (x > right - EDGE) return Math.ceil((x - (right - EDGE)) / 4);
  return 0;
};

const findHorizontalScroller = (from: HTMLElement): HTMLElement | null => {
  for (let el = from.parentElement; el; el = el.parentElement) {
    const overflowX = getComputedStyle(el).overflowX;
    if ((overflowX === 'auto' || overflowX === 'scroll') && el.scrollWidth > el.clientWidth) return el;
  }
  return null;
};

// The column being dragged is drawn as a copy hung off <body>, so it can be lifted anywhere in the window -
// moved inside the list it was clipped by the list and the app shell and slid under the toolbar. The copy takes
// the list wrapper's inline style (the theme's colour variables and background) and font, and each copied cell
// sits where the original is; the original stays behind, hidden, as the column's place in the row.
// The bottom of what can be seen of the list: the list is taller than the area that scrolls it, so its own
// box runs on under the pager. The nearest clipping ancestor's inner bottom (its horizontal scrollbar left out)
// is where the rows stop being visible.
const visibleBottom = (list: HTMLElement) => {
  let bottom = Math.min(list.getBoundingClientRect().bottom, window.innerHeight);
  for (let el = list.parentElement; el && el !== document.body; el = el.parentElement) {
    if (getComputedStyle(el).overflowY === 'visible') continue;
    const r = el.getBoundingClientRect();
    bottom = Math.min(bottom, r.top + el.clientTop + el.clientHeight);
  }
  return bottom;
};

// The background the list is drawn on. The ticket list and My Work paint it on their wrapper; the user page's
// Tickets tab (standalone) is transparent and shows the panel behind it, so the first solid one up the tree.
const solidBackground = (list: HTMLElement) => {
  for (let el: HTMLElement | null = list; el; el = el.parentElement) {
    const background = getComputedStyle(el).backgroundColor;
    if (background && background !== 'transparent' && !/^rgba\(.*,\s*0\)$/.test(background)) return background;
  }
  return '';
};

const buildColumnGhost = (els: HTMLElement[], list: HTMLElement) => {
  const top = els[0].getBoundingClientRect();
  const wrapper = (list.closest('.main-ticket-list') as HTMLElement) ?? list;
  const font = getComputedStyle(list);
  const ghost = document.createElement('div');
  ghost.setAttribute('style', wrapper.getAttribute('style') ?? '');
  ghost.className = 'tikit-column-ghost';
  Object.assign(ghost.style, {
    left: `${top.left}px`, top: `${top.top}px`, width: `${top.width}px`,
    height: `${Math.max(0, visibleBottom(list) - top.top)}px`,
    fontFamily: font.fontFamily, fontSize: font.fontSize, color: font.color,
    backgroundColor: solidBackground(list),
  });
  els.forEach(el => {
    const r = el.getBoundingClientRect();
    const copy = el.cloneNode(true) as HTMLElement;
    copy.classList.remove('tikit-column-sliding', 'tikit-column-placeholder');
    Object.assign(copy.style, {
      position: 'absolute', left: '0', top: `${r.top - top.top}px`, width: `${r.width}px`, height: `${r.height}px`,
      margin: '0', transform: '', boxSizing: 'border-box',
      // Text colour is set on each row (white in dark mode), not on the list, so it is taken from the row.
      color: getComputedStyle(el.parentElement ?? el).color,
    });
    ghost.appendChild(copy);
  });
  // The header's line-height (which centres the name) and its bottom border are set on the header row, not the
  // cell, so the copied header takes them from the row and its full height.
  const headerRow = els[0].closest('.ms-DetailsHeader') as HTMLElement;
  if (headerRow) {
    const row = getComputedStyle(headerRow);
    // Above the rows' copies, and solid: scrolled down, the rows that have gone up under the frozen header sit where it
    // is, and they showed through it while the column was dragged - drawn after it, and through it, since the header
    // row is transparent (the frozen wrapper around it is what paints the colour).
    Object.assign((ghost.firstElementChild as HTMLElement).style, {
      lineHeight: row.lineHeight, height: `${headerRow.getBoundingClientRect().height}px`,
      borderBottom: row.borderBottom, backgroundColor: solidBackground(headerRow), zIndex: '1',
    });
  }
  return ghost;
};

// Our own column drag, in place of Fluent's HTML5 one: during an HTML5 drag Windows draws the cursor and a page
// can only pick a move / copy / link badge, so the hand cursor (and columns sliding aside as you drag) needs the
// drag done by hand. A column is picked up anywhere on its header, as HubSpot's are - but not on its menu button,
// its sort arrow or the resize divider - and only once the pointer moves 4px, so a press that does not move is the sort click it
// always was. The header and the column's visible cells follow the pointer, the columns it passes slide aside,
// and on release onDrop gets the column's old and new position (the same positions Fluent's onColumnDrop used).
// Esc cancels; a drag never turns into a sort click. Fluent's own drag is still wired (columnReorderOptions) - it
// marks the headers draggable - but its grip is hidden (main.css) and its dragstart is cancelled.
// Whether a press is on a header's sort arrow, which sorts and is never a drag handle. Fluent's arrow on the sorted
// column is an element. The up/down hint on an unsorted one is the title's ::after (main.css), which nothing can
// match, so it is found by where it draws: straight after the column name, 6px of padding and a 12px glyph.
const SORT_HINT_WIDTH = 18;
const isOnSortArrow = (pressed: Element, cell: HTMLElement, x: number) => {
  if (pressed.closest('[data-icon-name="SortUp"], [data-icon-name="SortDown"]')) return true;
  if (!cell.matches('.is-sortable[aria-sort="none"]')) return false;
  const nameRight = cell.querySelector('.ms-DetailsHeader-cellName')?.getBoundingClientRect().right;
  return nameRight != null && x >= nameRight && x <= nameRight + SORT_HINT_WIDTH;
};

const startColumnDrag = (event: React.PointerEvent, onDrop: (draggedIndex: number, targetIndex: number) => void) => {
  if (event.button !== 0) return;
  const pressed = event.target as Element;
  // The menu button, and the open menu, which Northstar draws inside the header (so its items are in here too).
  if (pressed.closest?.('.column-menu-host')) return;
  if (pressed.closest?.('.ms-DetailsHeader')) window.dispatchEvent(new Event(HEADER_PRESSED));
  if (pressed.closest?.('.ms-DetailsHeader-cellSizer') || pressed.closest?.('.list-rows-refreshing')) return;
  const cell = pressed.closest?.<HTMLElement>('.ms-DetailsHeader-cell[draggable="true"]');
  const header = cell?.closest<HTMLElement>('.ms-DetailsHeader');
  const list = header?.closest<HTMLElement>('.ms-DetailsList');
  if (!cell || !header || !list || isOnSortArrow(pressed, cell, event.clientX)) return;
  const cells = Array.from(header.querySelectorAll<HTMLElement>('.ms-DetailsHeader-cell[draggable="true"]'));
  const from = cells.indexOf(cell);
  if (from < 0) return;
  event.preventDefault();
  event.stopPropagation();

  const rows = Array.from(list.querySelectorAll<HTMLElement>('.ms-DetailsRow-fields'));
  const columnEls = cells.map((headerCell, i) => [headerCell, ...rows.map(row => row.children[i] as HTMLElement).filter(Boolean)]);
  const rects = cells.map(c => c.getBoundingClientRect());
  const width = rects[from].width;
  // The landing spot, drawn the full height of the list - header to the last visible row - in the
  // resize divider's purple. It hangs off <body>: inside the app, an ancestor makes position: fixed
  // relative to itself (measured 221px / 136px out), so the theme's colour is read from the list instead
  // of inherited.
  const line = document.createElement('div');
  line.className = 'tikit-column-drop-line';
  line.style.background = getComputedStyle(list).getPropertyValue('--mgt-theme-brand-foreground').trim() || 'currentColor';
  line.style.top = `${rects[from].top}px`;
  line.style.height = `${Math.max(0, visibleBottom(list) - rects[from].top)}px`;
  const scroller = findHorizontalScroller(header);
  const startScroll = scroller?.scrollLeft ?? 0;
  const startX = event.clientX;
  const startY = event.clientY;
  let lastX = event.clientX;
  let lastY = event.clientY;
  let target = from;
  let moved = false;
  let autoScroll = 0;
  let ghost: HTMLElement | null = null;

  const setShift = (i: number, x: number, y = 0) =>
    columnEls[i].forEach(el => { el.style.transform = x || y ? `translate(${x}px, ${y}px)` : ''; });

  const update = () => {
    const scrolled = (scroller?.scrollLeft ?? 0) - startScroll;
    const dx = lastX - startX + scrolled;
    const dy = lastY - startY;
    if (!moved) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      moved = true;
      document.documentElement.classList.add('tikit-column-dragging');
      ghost = buildColumnGhost(columnEls[from], list);
      document.body.appendChild(ghost);
      columnEls.forEach((els, i) => els.forEach(el => el.classList.add(i === from ? 'tikit-column-placeholder' : 'tikit-column-sliding')));
      document.body.appendChild(line);
    }
    // The copy follows the pointer anywhere in the window; where the column lands follows left and right alone.
    ghost.style.transform = `translate(${lastX - startX}px, ${dy}px)`;
    // The slot moves as soon as the column's leading edge - its left edge going left, its right edge going
    // right - is ENTRY of the way into a neighbour, so the slot, and the line marking it, stay ahead of the
    // column. Measured against the middle instead, the column ran up to ~100px past the line.
    const entry = (i: number) => rects[i].width * ENTRY;
    target = dx < 0
      ? rects.filter((r, i) => i !== from && r.right - entry(i) <= rects[from].left + dx).length
      : rects.filter((r, i) => i !== from && r.left + entry(i) < rects[from].right + dx).length;
    columnEls.forEach((_, i) => {
      if (i === from) return;
      setShift(i, slideOffset(i, from, target, width));
    });
    // The slot it will land in, marked on its leading side - the left edge when dragging left, the right
    // edge when dragging right - so the line is always in front of the column, never behind it.
    const gapLeft = dropSlotLeft(rects, from, target, width);
    line.style.left = `${(dx > 0 ? gapLeft + width : gapLeft) - scrolled - 1}px`;
  };

  const tick = () => {
    if (!autoScroll || !scroller) return;
    scroller.scrollLeft += autoScroll;
    update();
    requestAnimationFrame(tick);
  };

  // Only the pointer that picked the column up moves it or puts it down - a second finger, or another pointer's
  // release, must not drop it.
  const pointerId = event.pointerId;
  const onMove = (e: PointerEvent) => {
    if (e.pointerId !== pointerId) return;
    lastX = e.clientX;
    lastY = e.clientY;
    update();
    if (scroller && moved) {
      const r = scroller.getBoundingClientRect();
      const speed = edgeScrollSpeed(e.clientX, r.left, r.right);
      const wasScrolling = autoScroll !== 0;
      autoScroll = speed;
      if (speed && !wasScrolling) requestAnimationFrame(tick);
    }
  };

  const finish = (drop: boolean) => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    window.removeEventListener('pointercancel', onCancel);
    window.removeEventListener('keydown', onKey, true);
    autoScroll = 0;
    columnEls.forEach(els => els.forEach(el => {
      el.style.transform = '';
      el.classList.remove('tikit-column-placeholder', 'tikit-column-sliding');
    }));
    document.documentElement.classList.remove('tikit-column-dragging');
    line.remove();
    ghost?.remove();
    // The click a release over the header fires straight after this belongs to the drag, not a sort. Released
    // anywhere else there is no click, so the flag expires once that moment has passed - left set, it ate the
    // next real click on a header.
    if (moved) {
      suppressNextClick = true;
      window.setTimeout(() => { suppressNextClick = false; }, 0);
    }
    if (drop && moved && target !== from) onDrop(from, target);
  };
  const onUp = (e: PointerEvent) => { if (e.pointerId === pointerId) finish(true); };
  // A touch the browser takes over for scrolling sends pointercancel and no pointerup. Left listening, the copy
  // stayed up and followed the pointer, and the next tap anywhere dropped the column (measured 2026-10-12).
  const onCancel = (e: PointerEvent) => { if (e.pointerId === pointerId) finish(false); };
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); finish(false); } };

  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointercancel', onCancel);
  window.addEventListener('keydown', onKey, true);
};

export const columnPointerDrag = (onDrop: (draggedIndex: number, targetIndex: number) => void) => ({
  onPointerDownCapture: (event: React.PointerEvent) => startColumnDrag(event, onDrop),
  onDragStartCapture: (event: React.DragEvent) => {
    if (!(event.target as Element).closest?.('.ms-DetailsHeader-cell')) return;
    event.preventDefault();
    event.stopPropagation();
  },
  onClickCapture: (event: React.MouseEvent) => {
    if (!suppressNextClick) return;
    suppressNextClick = false;
    event.preventDefault();
    event.stopPropagation();
  },
});

// Fluent's justified (auto-fit) layout ignores a passed calculatedWidth and restarts every column from
// minWidth, so the only way to give a column a width of our choosing there is the width Fluent itself
// remembers for a dragged column: DetailsListBase._columnOverrides. PRIVATE to Fluent 8 (8.34.1 here) -
// re-check after any upgrade.
//
// Each column gets its saved width, or else its usual default (maxWidth); a column with no maxWidth is the
// one that soaks up the spare width, so it is left alone. That lets the columns' minWidth drop to the drag
// floor without the layout changing - Fluent would otherwise start every column at the floor and hand all
// the spare width to the first uncapped column. A column already in Fluent's record (dragged in this
// session, or seeded before) keeps what it has. Returns whether anything was added; the caller then passes
// a fresh columns array so Fluent lays the columns out again.
export const seedAutoFitWidths = (
  detailsList: unknown,
  columns: { key: string; maxWidth?: number }[],
  widths: Record<string, number>
): boolean => {
  const overrides = (detailsList as { _columnOverrides?: Record<string, { calculatedWidth?: number; currentWidth?: number }> })?._columnOverrides;
  if (!overrides) return false;
  let seeded = false;
  for (const column of columns) {
    if (overrides[column.key]?.calculatedWidth) continue;
    const saved = widths[column.key];
    const width = typeof saved === 'number' && Number.isFinite(saved) ? saved : column.maxWidth;
    if (!width) continue;
    overrides[column.key] = { ...overrides[column.key], calculatedWidth: width, currentWidth: width };
    seeded = true;
  }
  return seeded;
};

// A column a list can sort shows the up/down hint on hover (main.css, .is-sortable). One it cannot sort is
// disabled: Fluent otherwise gives every header a hover background and click, so a column that does nothing
// on click still looked clickable. Disabling it does not stop it being dragged or resized.
export const markSortableColumns = <T extends IColumn>(columns: T[], canSort: (column: T) => boolean): T[] =>
  columns.map(column => canSort(column)
    ? { ...column, headerClassName: [column.headerClassName, 'is-sortable'].filter(Boolean).join(' ') }
    : { ...column, columnActionsMode: ColumnActionsMode.disabled });
